import JsBarcode from "jsbarcode";
import { jsPDF } from "jspdf";

export const FORMATS = [
  { value: "auto", label: "Auto" },
  { value: "EAN13", label: "EAN-13" },
  { value: "UPC", label: "UPC-A" },
  { value: "CODE128", label: "Code 128" },
];
export const FORMAT_LABEL = { EAN13: "EAN-13", UPC: "UPC-A", CODE128: "Code 128" };
export const LABEL_MM = { w: 50, h: 25 };

const checkDigitOk = (digits) => {
  const body = digits.slice(0, -1).split("").map(Number);
  // GTIN weighting: from the right of the body, odd positions x3
  const sum = body.reverse().reduce((s, d, i) => s + d * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === Number(digits.slice(-1));
};

// Returns { format, valid, warning } for the stored value (never modified).
export function analyse(value, override = "auto") {
  const v = value ?? "";
  if (!String(v).trim()) return { format: null, valid: false, warning: null };
  const s = String(v);
  const digits = /^\d+$/.test(s);
  const isEan = digits && s.length === 13 && checkDigitOk(s);
  const isUpc = digits && s.length === 12 && checkDigitOk(s);
  // eslint-disable-next-line no-control-regex
  const ascii = /^[\x00-\x7F]+$/.test(s);
  if (override === "EAN13") {
    if (isEan) return { format: "EAN13", valid: true, warning: null };
    return { format: "EAN13", valid: false, warning: digits && s.length === 13 ? "EAN-13 check digit is wrong" : "EAN-13 needs exactly 13 digits" };
  }
  if (override === "UPC") {
    if (isUpc) return { format: "UPC", valid: true, warning: null };
    return { format: "UPC", valid: false, warning: digits && s.length === 12 ? "UPC-A check digit is wrong" : "UPC-A needs exactly 12 digits" };
  }
  if (override === "CODE128" || override === "auto") {
    if (!ascii) return { format: "CODE128", valid: false, warning: "Code 128 only supports standard (ASCII) characters" };
    if (override === "auto" && isEan) return { format: "EAN13", valid: true, warning: null };
    if (override === "auto" && isUpc) return { format: "UPC", valid: true, warning: null };
    let warning = null;
    if (override === "auto" && digits && (s.length === 12 || s.length === 13)) warning = `Looks like ${s.length === 13 ? "EAN-13" : "UPC-A"} but the check digit is wrong, so it's shown as Code 128`;
    else if (s.length > 24) warning = "Long value — bars will be very narrow on a 50 mm label";
    return { format: "CODE128", valid: true, warning };
  }
  return { format: null, valid: false, warning: "Unknown format" };
}

const OPTS = { margin: 0, background: "#ffffff", lineColor: "#000000", font: "monospace", fontSize: 16, textMargin: 2 };

export function renderSvgString(value, format, { height = 60, displayValue = true } = {}) {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  let ok = true;
  JsBarcode(svg, String(value), { ...OPTS, format, height, displayValue, width: 2, valid: (v) => { ok = v; } });
  return ok ? new XMLSerializer().serializeToString(svg) : null;
}

export function renderCanvas(value, format, { height = 80, scale = 3, displayValue = true } = {}) {
  const canvas = document.createElement("canvas");
  let ok = true;
  JsBarcode(canvas, String(value), { ...OPTS, format, height: height * scale / 2, width: scale, fontSize: 16 * scale / 2, displayValue, margin: 4 * scale, valid: (v) => { ok = v; } });
  return ok ? canvas : null;
}

const safeName = (s) => String(s || "barcode").replace(/[^a-z0-9-_]+/gi, "_").slice(0, 60);
const trigger = (href, name) => { const a = document.createElement("a"); a.href = href; a.download = name; document.body.appendChild(a); a.click(); a.remove(); };

export function downloadSvg(value, format, name) {
  const s = renderSvgString(value, format);
  if (!s) return false;
  const url = URL.createObjectURL(new Blob([s], { type: "image/svg+xml" }));
  trigger(url, `${safeName(name)}.svg`);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return true;
}

export function downloadPng(value, format, name) {
  const c = renderCanvas(value, format);
  if (!c) return false;
  trigger(c.toDataURL("image/png"), `${safeName(name)}.png`);
  return true;
}

// items: [{ title, sku, barcode, format, qty }]
export function labelsPdf(items, fileName = "barcode-labels") {
  const { w, h } = LABEL_MM;
  const doc = new jsPDF({ unit: "mm", format: [w, h], orientation: "landscape" });
  let first = true;
  for (const it of items) {
    const c = renderCanvas(it.barcode, it.format, { displayValue: true });
    if (!c) continue;
    const img = c.toDataURL("image/png");
    const bw = w - 6;
    const bh = Math.min(h - 10, (c.height / c.width) * bw);
    for (let i = 0; i < Math.max(1, it.qty || 1); i++) {
      if (!first) doc.addPage([w, h], "landscape");
      first = false;
      doc.setFont("helvetica", "bold"); doc.setFontSize(7);
      doc.text(doc.splitTextToSize(it.title || "", w - 6)[0] || "", 3, 4);
      if (it.sku) { doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.text(`SKU ${it.sku}`, 3, 7); }
      doc.addImage(img, "PNG", (w - bw) / 2, h - bh - 2, bw, bh);
    }
  }
  doc.save(`${safeName(fileName)}.pdf`);
}

const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// Clean print window: 50x25mm pages, no app chrome, browser print dialog.
export function printLabels(items) {
  const labels = [];
  for (const it of items) {
    const svg = renderSvgString(it.barcode, it.format, { height: 50 });
    if (!svg) continue;
    for (let i = 0; i < Math.max(1, it.qty || 1); i++) {
      labels.push(`<div class="l"><div class="n">${esc(it.title)}</div>${it.sku ? `<div class="s">SKU ${esc(it.sku)}</div>` : ""}<div class="b">${svg}</div></div>`);
    }
  }
  if (!labels.length) return false;
  const w = window.open("", "_blank", "width=480,height=640");
  if (!w) return null;
  w.document.write(`<!doctype html><html><head><title>Barcode labels</title><style>
@page{size:${LABEL_MM.w}mm ${LABEL_MM.h}mm;margin:0}
*{box-sizing:border-box}html,body{margin:0;padding:0;font-family:Arial,Helvetica,sans-serif;color:#000;background:#fff}
.l{width:${LABEL_MM.w}mm;height:${LABEL_MM.h}mm;padding:1.5mm 2mm;display:flex;flex-direction:column;overflow:hidden;page-break-after:always;break-after:page}
.l:last-child{page-break-after:auto;break-after:auto}
.n{font-size:7pt;font-weight:bold;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;line-height:1.15}
.s{font-size:6pt;line-height:1.2}
.b{flex:1;min-height:0;display:flex;align-items:flex-end;justify-content:center}
.b svg{width:100%;height:100%;max-height:100%}
@media screen{body{background:#eee;padding:8px}.l{background:#fff;margin:0 auto 8px;box-shadow:0 1px 3px rgba(0,0,0,.2)}}
</style></head><body>${labels.join("")}<script>window.onload=function(){setTimeout(function(){window.focus();window.print();},150)}<\/script></body></html>`);
  w.document.close();
  return true;
}
